import React, { useEffect, useState } from "react";
import { Check, Share2 } from "lucide-react";

/**
 * Icon-only share control. Uses the native share sheet when available,
 * otherwise copies `text + url` to the clipboard.
 */
const ShareIconButton = ({
  url,
  text = "",
  title = "BetBond",
  className = "",
  size = 14,
  ariaLabel = "Share",
  onShared,
  onError,
}) => {
  const [state, setState] = useState("idle"); // idle | copied | error

  useEffect(() => {
    if (state === "idle") return undefined;
    const t = setTimeout(() => setState("idle"), 2200);
    return () => clearTimeout(t);
  }, [state]);

  const handleShare = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!url) {
      setState("error");
      onError?.("Could not build share link.");
      return;
    }

    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({
          title,
          text: text || undefined,
          url,
        });
        onShared?.("shared");
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }

    try {
      const payload = text ? `${text} ${url}` : url;
      await navigator.clipboard.writeText(payload);
      setState("copied");
      onShared?.("copied");
    } catch {
      setState("error");
      onError?.("Could not copy link.");
    }
  };

  const label = state === "copied" ? "Link copied" : ariaLabel;

  return (
    <button
      type="button"
      className={`bb-share-icon${state === "copied" ? " bb-share-icon-copied" : ""}${className ? ` ${className}` : ""}`}
      onClick={handleShare}
      aria-label={label}
      title={label}
    >
      {state === "copied" ? (
        <Check size={size} aria-hidden />
      ) : (
        <Share2 size={size} aria-hidden />
      )}
    </button>
  );
};

export default ShareIconButton;
